import { ACCESS_GROUPS, type AccessGroupRule } from "../../access-control/models/access-groups.models";
import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";

export const ADMIN_PATHS = {
  root: "/admin",
  dashboard: "/admin/dashboard",
  emails: "/admin/emails",
  aiFlows: "/admin/ai-flows",
  aiScans: "/admin/ai-scans",
  debug: "/admin/debug",
  debugCronJobs: "/admin/debug/cron-jobs",
  debugTechForm: "/admin/debug/tech-form",
  debugTechFormInbox: "/admin/debug/tech-form-inbox",
  debugOnboarding: "/admin/debug/onboarding",
  debugVoyagePnl: "/admin/debug/voyage-pnl",
  debugBalticQuestionnaire: "/admin/debug/baltic-questionnaire",
  monitoring: "/admin/monitoring",
  monitoringCronJobs: "/admin/monitoring/cron-jobs",
  monitoringDocuments: "/admin/monitoring/documents",
  monitoringVessels: "/admin/monitoring/vessels",
  monitoringTechForms: "/admin/monitoring/tech-forms",
  monitoringBalticQuestionnaires: "/admin/monitoring/baltic-questionnaires",
  monitoringVoyages: "/admin/monitoring/voyages",
  monitoringVoyagePnl: "/admin/monitoring/voyage-pnl",
  monitoringNotifications: "/admin/monitoring/notifications",
  monitoringUwhc: "/admin/monitoring/uwhc",
  uwhc: "/admin/uwhc",
  paintSchemes: "/admin/paint-schemes",
  voyagePnl: "/admin/voyage-pnl",
  uwhcSeniorVoUsers: "/admin/uwhc/senior-vo-users",
  certificatesSettings: "/admin/certificates-settings",
  kpis: "/admin/kpis",
  crewReport: "/admin/crew-report",
  keysSecrets: "/admin/keys-secrets",
  bqTqAdmin: "/admin/bq-tq-admin",
  bqTqAdminTechnicalForm: "/admin/bq-tq-admin/technical-form",
  bqTqAdminBalticQuestionnaire: "/admin/bq-tq-admin/baltic-questionnaire",
  accessControlRoleFeatures: "/admin/access-control/role-features",
} as const;

export type AdminPageId =
  | "aiFlows"
  | "aiScans"
  | "dashboard"
  | "emails"
  | "debug"
  | "monitoring"
  | "uwhc"
  | "paintSchemes"
  | "voyagePnl"
  | "certificatesSettings"
  | "kpis"
  | "crewReport"
  | "keysSecrets"
  | "bqTqAdmin"
  | "accessControl";
export type AdminSidebarDrawerId = "debug" | "monitoring" | "certificates" | "aiFlows" | "aiScans";
export type AdminDebugSectionId =
  | "cronJobs"
  | "techForm"
  | "techFormInbox"
  | "balticQuestionnaire"
  | "onboarding"
  | "voyagePnl";
export type AdminMonitoringSectionId =
  | "cronJobs"
  | "documents"
  | "vessels"
  | "techForms"
  | "balticQuestionnaires"
  | "voyages"
  | "voyagePnl"
  | "notifications"
  | "uwhc";

export interface AdminSidebarItem {
  id: AdminPageId;
  label: string;
  path: string;
  dataTest: string;
  requiredFeatureKey?: string;
  requiredFeatureKeys?: readonly string[];
  requiredGroups?: AccessGroupRule;
  additionalAccessRules?: readonly AdminSidebarFeatureAccessRule[];
}

export interface AdminSidebarDrawerSection {
  id: string;
  label: string;
  path: string;
  dataTest: string;
}

export interface AdminSidebarFeatureSection extends AdminSidebarDrawerSection {
  requiredFeatureKeys: readonly string[];
  requiredGroups?: AccessGroupRule;
  additionalAccessRules?: readonly AdminSidebarFeatureAccessRule[];
}

export interface AdminSidebarFeatureAccessRule {
  featureKeys: readonly string[];
  requiredGroups?: AccessGroupRule;
}

export interface AdminSidebarDrawerNavProps {
  groupId: AdminSidebarDrawerId;
  label: string;
  sections: readonly AdminSidebarDrawerSection[];
  isCollapsed: boolean;
  isActivePath: boolean;
  isDrawerOpen: boolean;
  isPopoverOpen: boolean;
  onToggleDrawer: () => void;
  onTogglePopover: () => void;
}

export interface AdminSidebarIconProps {
  id: AdminPageId | AdminSidebarDrawerId;
}

export interface AdminDebugSection extends AdminSidebarFeatureSection {
  id: AdminDebugSectionId;
}

export interface AdminMonitoringSection extends AdminSidebarFeatureSection {
  id: AdminMonitoringSectionId;
}

export const ADMIN_DEBUG_SECTIONS: AdminDebugSection[] = [
  {
    id: "cronJobs",
    label: "Cron jobs",
    path: ADMIN_PATHS.debugCronJobs,
    dataTest: "admin-sidebar-debug-cron-jobs-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING, FEATURE_KEYS.HOLD_CLEANING_ADMIN],
  },
  {
    id: "techForm",
    label: "Tech Form",
    path: ADMIN_PATHS.debugTechForm,
    dataTest: "admin-sidebar-debug-tech-form-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING],
  },
  {
    id: "techFormInbox",
    label: "Tech Form Inbox",
    path: ADMIN_PATHS.debugTechFormInbox,
    dataTest: "admin-sidebar-debug-tech-form-inbox-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING],
  },
  {
    id: "balticQuestionnaire",
    label: "Baltic Questionnaire",
    path: ADMIN_PATHS.debugBalticQuestionnaire,
    dataTest: "admin-sidebar-debug-baltic-questionnaire-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING],
  },
  {
    id: "onboarding",
    label: "Onboarding",
    path: ADMIN_PATHS.debugOnboarding,
    dataTest: "admin-sidebar-debug-onboarding-link",
    requiredFeatureKeys: [],
    requiredGroups: { allOf: [ACCESS_GROUPS.Admin] },
  },
  {
    id: "voyagePnl",
    label: "Voyage PnL",
    path: ADMIN_PATHS.debugVoyagePnl,
    dataTest: "admin-sidebar-debug-voyage-pnl-link",
    requiredFeatureKeys: [],
    requiredGroups: { allOf: [ACCESS_GROUPS.Admin] },
  },
];

export const ADMIN_MONITORING_SECTIONS: AdminMonitoringSection[] = [
  {
    id: "cronJobs",
    label: "Cron jobs",
    path: ADMIN_PATHS.monitoringCronJobs,
    dataTest: "admin-sidebar-monitoring-cron-jobs-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING, FEATURE_KEYS.HOLD_CLEANING_ADMIN],
  },
  {
    id: "documents",
    label: "Documents",
    path: ADMIN_PATHS.monitoringDocuments,
    dataTest: "admin-sidebar-monitoring-documents-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING],
  },
  {
    id: "vessels",
    label: "Vessels",
    path: ADMIN_PATHS.monitoringVessels,
    dataTest: "admin-sidebar-monitoring-vessels-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING],
  },
  {
    id: "techForms",
    label: "Tech Forms",
    path: ADMIN_PATHS.monitoringTechForms,
    dataTest: "admin-sidebar-monitoring-tech-forms-link",
    requiredFeatureKeys: [],
    additionalAccessRules: [
      {
        featureKeys: [FEATURE_KEYS.TQ_ADMIN],
        requiredGroups: { anyOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.VesselPerformance] },
      },
    ],
  },
  {
    id: "balticQuestionnaires",
    label: "Baltic Questionnaires",
    path: ADMIN_PATHS.monitoringBalticQuestionnaires,
    dataTest: "admin-sidebar-monitoring-baltic-questionnaires-link",
    requiredFeatureKeys: [],
    additionalAccessRules: [
      {
        featureKeys: [FEATURE_KEYS.TQ_ADMIN],
        requiredGroups: { anyOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.VesselPerformance] },
      },
    ],
  },
  {
    id: "voyages",
    label: "Voyages",
    path: ADMIN_PATHS.monitoringVoyages,
    dataTest: "admin-sidebar-monitoring-voyages-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING],
  },
  {
    id: "voyagePnl",
    label: "Voyage PnL",
    path: ADMIN_PATHS.monitoringVoyagePnl,
    dataTest: "admin-sidebar-monitoring-voyage-pnl-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING],
  },
  {
    id: "notifications",
    label: "Notifications",
    path: ADMIN_PATHS.monitoringNotifications,
    dataTest: "admin-sidebar-monitoring-notifications-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING],
  },
  {
    id: "uwhc",
    label: "Underwater Hull Cleaning",
    path: ADMIN_PATHS.monitoringUwhc,
    dataTest: "admin-sidebar-monitoring-uwhc-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_REF_PARAMETERS_UWHC_VENDORS],
  },
];

export const ADMIN_SIDEBAR_ITEMS: AdminSidebarItem[] = [
  {
    id: "aiFlows",
    label: "AI Lab",
    path: ADMIN_PATHS.aiFlows,
    dataTest: "admin-sidebar-ai-flows-link",
    requiredGroups: { allOf: [ACCESS_GROUPS.Admin] },
  },
  {
    id: "dashboard",
    label: "Dashboard",
    path: ADMIN_PATHS.dashboard,
    dataTest: "admin-sidebar-dashboard-link",
    requiredGroups: { allOf: [ACCESS_GROUPS.Admin] },
  },
  {
    id: "emails",
    label: "Email",
    path: ADMIN_PATHS.emails,
    dataTest: "admin-sidebar-emails-link",
    requiredGroups: { allOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.InternalUsers] },
  },
  {
    id: "debug",
    label: "Debug",
    path: ADMIN_PATHS.debug,
    dataTest: "admin-sidebar-debug-link",
    requiredFeatureKeys: [FEATURE_KEYS.ADMIN_DEBUG_MONITORING, FEATURE_KEYS.HOLD_CLEANING_ADMIN],
  },
  {
    id: "monitoring",
    label: "Monitoring",
    path: ADMIN_PATHS.monitoring,
    dataTest: "admin-sidebar-monitoring-link",
    requiredFeatureKeys: [
      FEATURE_KEYS.ADMIN_DEBUG_MONITORING,
      FEATURE_KEYS.ADMIN_REF_PARAMETERS_UWHC_VENDORS,
      FEATURE_KEYS.HOLD_CLEANING_ADMIN,
    ],
    additionalAccessRules: [
      {
        featureKeys: [FEATURE_KEYS.TQ_ADMIN],
        requiredGroups: { anyOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.VesselPerformance] },
      },
    ],
  },
  {
    id: "uwhc",
    label: "UWHC",
    path: ADMIN_PATHS.uwhc,
    dataTest: "admin-sidebar-uwhc-link",
    requiredFeatureKey: FEATURE_KEYS.ADMIN_REF_PARAMETERS_UWHC_VENDORS,
  },
  {
    id: "paintSchemes",
    label: "Paints Scheme",
    path: ADMIN_PATHS.paintSchemes,
    dataTest: "admin-sidebar-paint-schemes-link",
    requiredFeatureKey: FEATURE_KEYS.ADMIN_REF_PARAMETERS_PAINT_SCHEMES,
  },
  {
    id: "voyagePnl",
    label: "Voyage PnL",
    path: ADMIN_PATHS.voyagePnl,
    dataTest: "admin-sidebar-voyage-pnl-link",
    requiredFeatureKey: FEATURE_KEYS.ADMIN_REF_PARAMETERS_SENIOR_VO,
  },
  {
    id: "certificatesSettings",
    label: "Settings",
    path: ADMIN_PATHS.certificatesSettings,
    dataTest: "admin-sidebar-certificates-settings-link",
    requiredFeatureKey: FEATURE_KEYS.ADMIN_REF_PARAMETERS_CERTS,
  },
  {
    id: "kpis",
    label: "KPIs",
    path: ADMIN_PATHS.kpis,
    dataTest: "admin-sidebar-kpis-link",
    requiredFeatureKey: FEATURE_KEYS.ADMIN_ANALYTICS_CERTS,
  },
  {
    id: "crewReport",
    label: "Crew Report",
    path: ADMIN_PATHS.crewReport,
    dataTest: "admin-sidebar-crew-report-link",
    requiredFeatureKey: FEATURE_KEYS.ADMIN_CREW_REPORT,
  },
  {
    id: "keysSecrets",
    label: "Keys & Secrets",
    path: ADMIN_PATHS.keysSecrets,
    dataTest: "admin-sidebar-keys-secrets-link",
    requiredGroups: { anyOf: [ACCESS_GROUPS.Admin] },
  },
  {
    id: "bqTqAdmin",
    label: "BQ/TQ",
    path: ADMIN_PATHS.bqTqAdmin,
    dataTest: "admin-sidebar-bq-tq-admin-link",
    requiredFeatureKey: FEATURE_KEYS.TQ_ADMIN,
    requiredGroups: { anyOf: [ACCESS_GROUPS.Admin, ACCESS_GROUPS.VesselPerformance] },
  },
  {
    id: "accessControl",
    label: "Access Control",
    path: ADMIN_PATHS.accessControlRoleFeatures,
    dataTest: "admin-sidebar-access-control-link",
    requiredFeatureKey: FEATURE_KEYS.ADMIN_ACCESS_CONTROL_ROLE_FEATURES,
  },
  {
    id: "aiScans",
    label: "AI Scans",
    path: ADMIN_PATHS.aiScans,
    dataTest: "admin-sidebar-ai-scans-link",
    requiredGroups: { allOf: [ACCESS_GROUPS.Admin] },
  },
];
